import React, { useState, useEffect, useRef } from 'react';
import {
    styled,
    Box,
    Stack,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Modal,
    CircularProgress,
    Button,
    Chip,
    //
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { fetchOptions } from '../../queries';
import { SelectModal } from '.';

const ChipWrap = styled(Box)(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    backgroundColor: '#F7F8FB',
    padding: 6,
    marginTop: 12,
    borderRadius: 5,
}));

const UserExceptions = ({ props }) => {
    const { role, label, excludeField, includeField } = props;
    const { clearErrors, setError, setValue, getValues } = useFormContext();
    const [name, setName] = useState(excludeField);
    const [options, setOptions] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [selectionMode, setSelectionMode] = useState('None');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const selectedMode = selectionMode === 'Selected';
    const excludeValue = getValues(excludeField);
    const includeValue = getValues(includeField);

    const handleRuleChange = (event) => {
        const { value } = event.target;
        setName(value);

        if (value === excludeField) {
            setSelectionMode('None');
            setValue(includeField, []);
        } else if (value === includeField) {
            setSelectionMode('All');
            setValue(excludeField, []);
        }

        setSelectedUsers([]);
        clearErrors();
    };

    const handleSelectionModeChange = (event) => {
        const { value } = event.target;
        setSelectionMode(value);
        setSelectedUsers([]);

        if (value === 'Selected') {
            setOpenModal(true);
        }
    };

    const handleDeleteUser = (userId) => {
        setSelectedUsers((prevSelected) => prevSelected.filter((id) => id !== userId));
    };

    const setInitialValues = (field, value) => {
        setName(field);
        setSelectedUsers(value);
        setSelectionMode('Selected');
        setValue(field, value);
    };

    const isInDefaultState = () => {
        return name === excludeField && selectionMode === 'None' && selectedUsers.length === 0;
    };

    useEffect(() => {
        const getOptions = async () => {
            const optionsResponse = await fetchOptions({
                endpoint: '/users',
                where: {
                    role,
                    subuser: { eq: false },
                    status: { neq: 'Inactive' },
                },
            });
            if (optionsResponse.error) {
                setOptions([]);
                setError(name, { type: 'fetch options', message: `Could not fetch ${role} options` });
            } else {
                setOptions(optionsResponse);
                clearErrors([name]);
            }
        };

        if (options.length < 1) {
            getOptions();
        }
    });

    useEffect(() => {
        setValue(name, selectedUsers);
    }, [selectedUsers]);

    useEffect(() => {
        // Only set initial values if component is still in default state
        if (!isInDefaultState()) {
            return;
        }

        if (excludeValue && excludeValue.length > 0) {
            setInitialValues(excludeField, excludeValue);
        } else if (includeValue && includeValue.length > 0) {
            setInitialValues(includeField, includeValue);
        }
    }, [excludeValue, includeValue, excludeField, includeField]);

    if (options.length < 1) {
        return <CircularProgress />;
    }

    return (
        <>
            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <>
                    <SelectModal
                        label={label}
                        options={options}
                        setOpenModal={setOpenModal}
                        selectedUsers={selectedUsers}
                        setSelectedUsers={setSelectedUsers}
                        //
                    />
                </>
            </Modal>
            <Box
                paddingTop={2}
                paddingBottom={2}
                paddingLeft={role === 'transport' ? 1 : 0}
                paddingRight={role === 'producer' ? 1 : 0}
                //
            >
                <Stack
                    spacing={3}
                    sx={{
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: 2,
                        padding: 3,
                    }}
                >
                    <Typography variant="body1" fontWeight={600} sx={{ opacity: 0.6 }}>
                        {label}
                    </Typography>
                    <FormControl>
                        <InputLabel>Rule</InputLabel>
                        <Select value={name} onChange={handleRuleChange} label="Rule">
                            <MenuItem value={excludeField}>Exclude</MenuItem>
                            <MenuItem value={includeField}>Include</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <InputLabel>{label}</InputLabel>
                        <Select value={selectionMode} onChange={handleSelectionModeChange} label={label}>
                            {name === includeField && <MenuItem value="All">All</MenuItem>}
                            {name === excludeField && <MenuItem value="None">None</MenuItem>}
                            <MenuItem value="Selected">Selected</MenuItem>
                        </Select>
                    </FormControl>
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="start">
                            <Typography variant="body2" fontWeight={600}>
                                {label}: {selectedMode ? selectedUsers.length : options.length}/{options.length}
                            </Typography>
                            {selectedMode && (
                                <Button onClick={() => setOpenModal(true)} variant="outlined" color="primary">
                                    Add
                                </Button>
                            )}
                        </Box>
                        {selectedMode && selectedUsers.length > 0 && (
                            <ChipWrap>
                                {selectedUsers.map((userId) => {
                                    const matchedOption = options.find((option) => option._id === userId);
                                    if (matchedOption) {
                                        return (
                                            <Chip
                                                key={matchedOption._id}
                                                label={matchedOption.name}
                                                onDelete={() => handleDeleteUser(matchedOption._id)}
                                                style={{ margin: 2 }}
                                                //
                                            />
                                        );
                                    }
                                    return null;
                                })}
                            </ChipWrap>
                        )}
                    </Box>
                </Stack>
            </Box>
        </>
    );
};

export default UserExceptions;
